import { sharedPoll, stopPoll } from '@/command/poll'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Activity,
  AlertTriangle,
  Cpu,
  Database,
  HardDrive,
  MemoryStick,
  RefreshCw,
  Server,
} from 'lucide-react'
import { metricsApi } from '@/api/metrics'
import { waProxy } from '@/lib/whatsappProxy'
import type { MetricsSeriesRow } from '@/types'

// ── Types ─────────────────────────────────────────────────────────
interface HealthData {
  status: string
  version?: string
  hostname?: string
  sessions: number
  connected: number
  uptime: number
  memoryMB: number
  totalMemoryMB?: number
  freeMemoryMB?: number
  loadAvg?: { '1min': number; '5min': number; '15min': number }
  disk?: { totalGB: number; usedGB: number; freeGB: number; percentUsed: number }
  recentErrors?: number
}

type ComponentStatus = 'healthy' | 'degraded' | 'offline' | 'unknown'

interface ComponentDef {
  id: string
  name: string
  description: string
  primaryMetric: string
  sparkMetrics: string[]
  icon: React.ReactNode
  accent: string
}

const COMPONENTS: ComponentDef[] = [
  {
    id: 'db',
    name: 'Database',
    description: 'Supabase Postgres — connections & slow queries',
    primaryMetric: 'active_conns',
    sparkMetrics: ['active_conns', 'slow_queries'],
    icon: <Database size={20} strokeWidth={1.75} />,
    accent: '#6366f1',
  },
  {
    id: 'wa-prod-1',
    name: 'wa-prod-1',
    description: 'WhatsApp production server (Nuremberg)',
    primaryMetric: 'load_1m',
    sparkMetrics: ['load_1m', 'mem_pct', 'disk_pct', 'error_count'],
    icon: <Server size={20} strokeWidth={1.75} />,
    accent: '#10b981',
  },
]

// ── Helpers ───────────────────────────────────────────────────────
function statusColor(s: ComponentStatus): string {
  switch (s) {
    case 'healthy': return '#22c55e'
    case 'degraded': return '#eab308'
    case 'offline': return '#ef4444'
    default: return '#6b7280'
  }
}

function statusLabel(s: ComponentStatus): string {
  switch (s) {
    case 'healthy': return 'Healthy'
    case 'degraded': return 'Degraded'
    case 'offline': return 'Offline'
    default: return 'Unknown'
  }
}

function deriveStatus(
  componentId: string,
  series: MetricsSeriesRow[],
  liveWa?: HealthData | null,
): ComponentStatus {
  if (componentId === 'wa-prod-1') {
    if (liveWa) {
      if (liveWa.status !== 'ok') return 'offline'
      const load = liveWa.loadAvg?.['1min'] ?? 0
      const memPct =
        liveWa.totalMemoryMB && liveWa.freeMemoryMB
          ? ((liveWa.totalMemoryMB - liveWa.freeMemoryMB) / liveWa.totalMemoryMB) * 100
          : 0
      const diskPct = liveWa.disk?.percentUsed ?? 0
      const errors = liveWa.recentErrors ?? 0
      if (load > 0.8 || memPct > 85 || diskPct > 85 || errors > 0) return 'degraded'
      return 'healthy'
    }
    const latestLoad = latestValue(series, 'wa-prod-1', 'load_1m')
    const latestErr = latestValue(series, 'wa-prod-1', 'error_count')
    if (latestLoad == null && latestErr == null) return 'unknown'
    if ((latestErr ?? 0) > 0 || (latestLoad ?? 0) > 0.8) return 'degraded'
    return 'healthy'
  }

  if (componentId === 'db') {
    const slow = latestValue(series, 'db', 'slow_queries')
    const conns = latestValue(series, 'db', 'active_conns')
    if (slow == null && conns == null) return 'unknown'
    if ((slow ?? 0) > 0) return 'degraded'
    return 'healthy'
  }

  return 'unknown'
}

function latestValue(rows: MetricsSeriesRow[], component: string, metric: string): number | null {
  const filtered = rows
    .filter((r) => r.component === component && r.metric === metric)
    .sort((a, b) => new Date(b.bucket_hour).getTime() - new Date(a.bucket_hour).getTime())
  const row = filtered[0]
  if (!row) return null
  return Number(row.max_value ?? row.avg_value ?? 0)
}

function sparkPoints(
  rows: MetricsSeriesRow[],
  component: string,
  metric: string,
): number[] {
  return rows
    .filter((r) => r.component === component && r.metric === metric)
    .sort((a, b) => new Date(a.bucket_hour).getTime() - new Date(b.bucket_hour).getTime())
    .map((r) => Number(r.avg_value ?? r.max_value ?? 0))
}

function ProgressBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0
  const barColor = pct > 85 ? '#ef4444' : pct > 65 ? '#eab308' : color
  return (
    <div className="w-full rounded-full overflow-hidden" style={{ height: 5, background: '#2d3148' }}>
      <div style={{ width: `${pct}%`, background: barColor, height: '100%', transition: 'width 0.5s ease' }} />
    </div>
  )
}

function Sparkline({ points, color }: { points: number[]; color: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || points.length < 2) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const w = canvas.width
    const h = canvas.height
    ctx.clearRect(0, 0, w, h)

    const min = Math.min(...points)
    const max = Math.max(...points)
    const range = max - min || 1
    const step = w / (points.length - 1)

    ctx.beginPath()
    ctx.strokeStyle = color
    ctx.lineWidth = 1.5
    points.forEach((p, i) => {
      const x = i * step
      const y = h - ((p - min) / range) * (h - 4) - 2
      if (i === 0) ctx.moveTo(x, y)
      else ctx.lineTo(x, y)
    })
    ctx.stroke()
  }, [points, color])

  if (points.length < 2) {
    return (
      <div className="h-10 flex items-center text-[10px]" style={{ color: '#6b7280' }}>
        No rollup data yet
      </div>
    )
  }

  return <canvas ref={canvasRef} width={120} height={40} className="w-full max-w-[140px] h-10" />
}

// ── Main page ─────────────────────────────────────────────────────
export default function HealthPage() {
  const [series, setSeries] = useState<MetricsSeriesRow[]>([])
  const [waLive, setWaLive] = useState<HealthData | null>(null)
  const [waError, setWaError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null)
  const [countdown, setCountdown] = useState(30)

  const since = useMemo(
    () => new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
    [],
  )

  const fetchAll = useCallback(async (manual = false) => {
    if (document.visibilityState!=='visible') return
    let failed=false
    if (manual) setRefreshing(true)
    try {
      const rows = await metricsApi.series({ since })
      setSeries(rows)
    } catch (e) {
      console.error('[HealthPage] metrics series:', e);failed=true
    }

    try {
      const h = await waProxy<HealthData>('/health', 'GET')
      setWaLive(h)
      setWaError(null)
    } catch (e) {
      failed=true;setWaLive(null)
      setWaError(e instanceof Error ? e.message : 'WA health fetch failed')
    }

    setLoading(false)
    setRefreshing(false)
    setLastRefresh(new Date())
    setCountdown(30)
    return !failed
  }, [since])

  useEffect(() => {
    const poll = sharedPoll(fetchAll, 30_000, true)
    const tick = setInterval(() => setCountdown((c) => Math.max(0, c - 1)), 1_000)
    return () => {
      stopPoll(poll)
      clearInterval(tick)
    }
  }, [fetchAll])

  const dbConns = latestValue(series, 'db', 'active_conns')
  const waMemPct =
    waLive?.totalMemoryMB && waLive?.freeMemoryMB
      ? Math.round(((waLive.totalMemoryMB - waLive.freeMemoryMB) / waLive.totalMemoryMB) * 100)
      : latestValue(series, 'wa-prod-1', 'mem_pct')
  const waDiskPct = waLive?.disk?.percentUsed ?? latestValue(series, 'wa-prod-1', 'disk_pct')
  const waLoad = waLive?.loadAvg?.['1min'] ?? latestValue(series, 'wa-prod-1', 'load_1m')
  const waErrors = waLive?.recentErrors ?? latestValue(series, 'wa-prod-1', 'error_count')

  const healthyCount = COMPONENTS.filter(
    (c) => deriveStatus(c.id, series, waLive) === 'healthy',
  ).length

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Health</h1>
          <p className="text-sm mt-1" style={{ color: '#9ca3af' }}>
            Platform metrics spine · {healthyCount}/{COMPONENTS.length} healthy
            {lastRefresh && ` · updated ${lastRefresh.toLocaleTimeString()}`}
          </p>
        </div>
        <button
          onClick={() => fetchAll(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors"
          style={{
            background: '#252836',
            border: '1px solid #2d3148',
            color: refreshing ? '#6b7280' : '#d1d5db',
          }}
        >
          <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
          {refreshing ? 'Refreshing…' : `Refresh (${countdown}s)`}
        </button>
      </div>

      {/* Component grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {COMPONENTS.map((comp) => {
          const status = deriveStatus(comp.id, series, waLive)
          const dot = statusColor(status)
          const primary = latestValue(series, comp.id, comp.primaryMetric)

          return (
            <div
              key={comp.id}
              className="rounded-xl p-5 flex flex-col gap-4"
              style={{ background: '#252836', border: '1px solid #2d3148' }}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      background: `${comp.accent}20`,
                      color: comp.accent,
                      border: `1px solid ${comp.accent}50`,
                    }}
                  >
                    {comp.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-white text-sm">{comp.name}</h3>
                    <p className="text-[11px] mt-0.5" style={{ color: '#6b7280' }}>{comp.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span
                    className="inline-block h-2.5 w-2.5 rounded-full"
                    style={{
                      background: dot,
                      boxShadow: status === 'healthy' ? `0 0 6px ${dot}` : 'none',
                    }}
                  />
                  <span className="text-xs" style={{ color: '#9ca3af' }}>{statusLabel(status)}</span>
                </div>
              </div>

              <div className="text-xs font-mono" style={{ color: '#9ca3af' }}>
                {comp.primaryMetric}: {primary != null ? primary : '—'}
              </div>

              <div className="grid grid-cols-2 gap-3">
                {comp.sparkMetrics.map((metric) => (
                  <div key={metric} className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wide" style={{ color: '#6b7280' }}>
                      {metric}
                    </div>
                    <Sparkline
                      points={sparkPoints(series, comp.id, metric)}
                      color={comp.accent}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Load gauges panel */}
      <div
        className="rounded-xl p-5 space-y-5"
        style={{ background: '#252836', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center gap-2">
          <Activity size={16} style={{ color: '#6366f1' }} />
          <h2 className="text-sm font-semibold text-white">Live gauges</h2>
        </div>

        {waError && (
          <div
            className="flex items-center gap-2 text-xs px-3 py-2 rounded-lg"
            style={{ background: '#ef444415', color: '#f87171' }}
          >
            <AlertTriangle size={13} />
            WA live probe: {waError}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#6b7280' }}>
              <Database size={11} />
              <span>DB connections</span>
            </div>
            <div className="text-lg font-semibold text-white font-mono">
              {dbConns ?? '—'}
            </div>
            <ProgressBar value={dbConns ?? 0} max={100} color="#6366f1" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#6b7280' }}>
              <Cpu size={11} />
              <span>WA CPU load (1m)</span>
            </div>
            <div className="text-lg font-semibold text-white font-mono">
              {waLoad ?? '—'}
            </div>
            <ProgressBar value={waLoad ?? 0} max={1} color="#f59e0b" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#6b7280' }}>
              <MemoryStick size={11} />
              <span>WA memory %</span>
            </div>
            <div className="text-lg font-semibold text-white font-mono">
              {waMemPct != null ? `${waMemPct}%` : '—'}
            </div>
            <ProgressBar value={waMemPct ?? 0} max={100} color="#6366f1" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px]" style={{ color: '#6b7280' }}>
              <HardDrive size={11} />
              <span>WA disk % · errors (1h)</span>
            </div>
            <div className="text-lg font-semibold text-white font-mono">
              {waDiskPct != null ? `${waDiskPct}%` : '—'}
              <span className="text-sm text-gray-500"> · </span>
              {waErrors ?? '—'} err
            </div>
            <ProgressBar value={waDiskPct ?? 0} max={100} color="#f59e0b" />
          </div>
        </div>

        {waLive && (
          <div className="text-[11px] font-mono pt-2" style={{ color: '#6b7280', borderTop: '1px solid #2d3148' }}>
            WA live: {waLive.connected}/{waLive.sessions} sessions · v{waLive.version ?? '?'} · {waLive.hostname ?? 'wa-prod-1'}
          </div>
        )}
      </div>

      {/* Anomaly placeholder — P3 populates */}
      <div
        id="health-anomalies"
        className="rounded-xl p-5"
        style={{ background: '#252836', border: '1px solid #2d3148' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle size={16} style={{ color: '#9ca3af' }} />
          <h2 className="text-sm font-semibold text-white">Anomalies</h2>
        </div>
        <p className="text-sm" style={{ color: '#6b7280' }}>
          No open anomalies. Anomaly detection ships in P3.
        </p>
      </div>

      {loading && (
        <p className="text-xs text-center" style={{ color: '#6b7280' }}>Loading metrics…</p>
      )}
    </div>
  )
}
